// Catalog.ID Client — Sharing Pane
// ============================================================================
// Opened from claim detail. Shows outgoing shares and allows sharing with new recipients.

import { createElement, createEmptyState, createFieldGroup, createFormRow, renderFormField } from '@catalog-shell';
import type { PaneType, PaneTypeRenderContext, FormRendererResult } from '@catalog-shell';
import * as claimsApi from '../api/claims';
import { ApiError, formatApiError } from '../api/client';
import { shareClaimWithRecipient } from '../state/claim-sharing';
import { PANE_IDS } from '../shell-config';

export function createSharingPane(): PaneType {
    return {
        typeId: PANE_IDS.sharing,
        kind: 'leaf',
        defaultTitle: 'Sharing',
        capabilities: [],
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });

            const claimUuid = ctx.instance.context['claimUuid'] as string;
            if (!claimUuid) {
                container.appendChild(createEmptyState({ message: 'No claim selected' }));
                return container;
            }

            // Share form
            const shareGroup = createFieldGroup({ title: 'Share This Claim', labelSize: 'l' });

            const recipientRow = createFormRow({ label: 'Recipient' });
            let recipientField: FormRendererResult;
            recipientField = renderFormField(
                { type: 'text', key: 'recipient', label: 'Recipient', placeholder: 'Catalog.ID (e.g., abc1234efg)' },
                '',
                () => {},
            );
            recipientRow.content.appendChild(recipientField.element);
            shareGroup.body.appendChild(recipientRow.element);

            const actionRow = createFormRow({ label: '' });
            const shareBtn = createElement('button', {
                className: 'cs-btn cs-btn--yes',
                textContent: 'Share',
            });
            const errorEl = createElement('div', { className: 'cid-pane-error' });

            shareBtn.addEventListener('click', async () => {
                const recipientCid = String(recipientField.getValue()).trim();
                if (!recipientCid) return;

                errorEl.classList.remove('cid-pane-error--visible');
                shareBtn.setAttribute('disabled', '');
                shareBtn.textContent = 'Sharing...';

                try {
                    // The re-wrap sequence lives in `state/claim-sharing.ts` —
                    // the chat contact flow performs the identical one from a
                    // key-grant handler, and a second copy of a rotation-chain
                    // walk is a bug waiting for the next rotation.
                    const outcome = await shareClaimWithRecipient(claimUuid, recipientCid);
                    ctx.shell.toast(
                        outcome === 'already-shared'
                            ? `Already shared with ${recipientCid}`
                            : `Claim shared with ${recipientCid}`,
                        'success');
                    recipientField.setValue('');
                    void renderSharesList(sharesContainer, claimUuid, ctx);
                } catch (err) {
                    let message = 'Sharing failed';
                    if (err instanceof ApiError && err.status === 404) {
                        message = `Account "${recipientCid}" not found. Check the Catalog.ID and try again.`;
                    } else if (err instanceof Error) {
                        message = err.message;
                    }
                    errorEl.textContent = message;
                    errorEl.classList.add('cid-pane-error--visible');
                } finally {
                    shareBtn.removeAttribute('disabled');
                    shareBtn.textContent = 'Share';
                }
            });

            actionRow.content.appendChild(shareBtn);
            shareGroup.body.appendChild(actionRow.element);
            shareGroup.body.appendChild(errorEl);

            container.appendChild(shareGroup.element);

            // Current shares list
            const sharesContainer = createElement('div');
            container.appendChild(sharesContainer);
            void renderSharesList(sharesContainer, claimUuid, ctx);

            return container;
        },
    };
}

/**
 * Whether a claimkey row may be unshared from this pane.
 *
 * Only true shares with a named recipient qualify. Validator rows carry
 * `recipientType='validator'` and a null CID — they render as a bare
 * "validator" label with no control, since there is no recipient CID to name
 * in the request. Operator review keys are stored as `recipientType='share'`
 * and are indistinguishable from user shares here (chat migration plan §4), so
 * the control DOES appear for them and the server's 400 refusal is what stops
 * a user from breaking a publication mid-review.
 */
export function isRemovableShare(
    share: { recipientType: string; recipientCid: string | null },
): boolean {
    return share.recipientType === 'share' && !!share.recipientCid;
}

async function renderSharesList(
    container: HTMLElement, claimUuid: string, ctx: PaneTypeRenderContext,
): Promise<void> {
    container.innerHTML = '';

    const group = createFieldGroup({ title: 'Shared With' });
    const listError = createElement('div', { className: 'cid-pane-error' });

    try {
        const detail = await claimsApi.getClaim(claimUuid);
        const shares = detail.keys.filter(k => k.recipientType !== 'self');

        if (shares.length === 0) {
            group.body.appendChild(createEmptyState({
                message: 'Not shared',
                description: 'This claim has not been shared with anyone.',
            }));
        } else {
            for (const share of shares) {
                const row = createElement('div', { className: 'cid-share-item' });
                row.appendChild(createElement('span', {
                    className: 'cid-share-cid',
                    textContent: share.recipientCid ?? share.recipientType,
                }));

                if (isRemovableShare(share)) {
                    const recipientCid = share.recipientCid as string;
                    const removeBtn = createElement('button', {
                        className: 'cs-btn cs-btn--no cs-btn--small',
                        textContent: 'Remove',
                    });
                    removeBtn.addEventListener('click', async () => {
                        const ok = await ctx.shell.confirm({
                            title: 'Remove Share',
                            message:
                                `Withdraw ${recipientCid}'s access to this claim? They will no `
                                + 'longer be able to decrypt it. Anything they already read or '
                                + 'copied stays with them, and cached copies may take up to a '
                                + 'day to expire.',
                            confirmLabel: 'Remove Share',
                            confirmVariant: 'no',
                        });
                        if (!ok) return;

                        listError.classList.remove('cid-pane-error--visible');
                        removeBtn.setAttribute('disabled', '');
                        removeBtn.textContent = 'Removing...';
                        try {
                            await claimsApi.revokeShare(claimUuid, { recipientCid });
                            ctx.shell.toast(`Share with ${recipientCid} removed`, 'success');
                            await renderSharesList(container, claimUuid, ctx);
                        } catch (err) {
                            // 400 = the server refused this recipient (own CID, operator
                            // review key); 404 = claim or share already gone. Both carry
                            // a message written to be shown as-is.
                            const message = formatApiError(err, 'Failed to remove share');
                            listError.textContent = message;
                            listError.classList.add('cid-pane-error--visible');
                            ctx.shell.toast(message, 'error');
                            removeBtn.removeAttribute('disabled');
                            removeBtn.textContent = 'Remove';
                        }
                    });
                    row.appendChild(removeBtn);
                }

                group.body.appendChild(row);
            }
        }
    } catch {
        group.body.appendChild(createElement('div', {
            className: 'cid-pane-error cid-pane-error--visible',
            textContent: 'Failed to load shares',
        }));
    }

    group.body.appendChild(listError);
    container.appendChild(group.element);
}
